import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getProtocol, searchProtocols, getDefaultSuggestions } from '../lib/triage/protocolLoader';
import { QUESTIONS, getAgeBand } from '../lib/triage/questions';
import { evaluate, hasEmergencyRedFlag } from '../lib/triage/triageEngine';
import { parseIntake } from '../lib/ai/aiService';
import { getState } from '../lib/userStore';
import { EMERGENCY_KEYWORDS } from '../lib/ai/systemPrompt';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isEmergencyText(text) {
  const lower = text.toLowerCase();
  return EMERGENCY_KEYWORDS.some(k => lower.includes(k));
}

// ─── Who-is-this-for toggle ───────────────────────────────────────────────────

function PersonToggle({ value, onChange }) {
  const OPTIONS = [
    { value: 'self', label: 'Me' },
    { value: 'child', label: 'My child' },
    { value: 'other', label: 'Someone else' },
  ];
  return (
    <div style={s.toggleRow}>
      {OPTIONS.map(opt => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            ...s.toggleBtn,
            background: value === opt.value ? 'var(--teal)' : 'var(--cream-2)',
            color: value === opt.value ? '#fff' : 'var(--ink-2)',
            fontWeight: value === opt.value ? 700 : 500,
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Suggestion row ───────────────────────────────────────────────────────────

function SuggestionRow({ protocol, onSelect }) {
  return (
    <button style={s.suggestionRow} onClick={() => onSelect(protocol)}>
      <span style={s.suggestionLabel}>{protocol.label}</span>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M9 18l6-6-6-6" stroke="var(--ink-4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
  );
}

// ─── Question renderers ───────────────────────────────────────────────────────

function BooleanQuestion({ question, onAnswer }) {
  return (
    <div style={s.answerGrid}>
      <button style={{ ...s.answerBtn, ...s.answerBtnYes }} onClick={() => onAnswer(true)}>
        Yes
      </button>
      <button style={{ ...s.answerBtn, ...s.answerBtnNo }} onClick={() => onAnswer(false)}>
        No
      </button>
    </div>
  );
}

function SelectQuestion({ question, onAnswer }) {
  return (
    <div style={s.answerStack}>
      {question.options.map(opt => (
        <button key={opt.value} style={s.selectBtn} onClick={() => onAnswer(opt.value)}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function NumberQuestion({ question, value, onChange, onSubmit }) {
  return (
    <div style={s.numberRow}>
      <input
        type="number"
        inputMode="numeric"
        min={question.min}
        max={question.max}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="—"
        style={s.numberInput}
        autoFocus
      />
      {question.unit && <span style={s.numberUnit}>{question.unit}</span>}
      <button
        style={{ ...s.answerBtn, ...s.answerBtnYes, flex: 'none', paddingLeft: 24, paddingRight: 24 }}
        onClick={() => { if (value !== '') onSubmit(Number(value)); }}
        disabled={value === ''}
      >
        Continue
      </button>
    </div>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────────

export default function GetCareScreen({ navigate, goBack }) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [phase, setPhase] = useState('intake'); // 'intake' | 'questions' | 'loading'
  const [inputText, setInputText] = useState('');
  const [personType, setPersonType] = useState('self');
  const [suggestions, setSuggestions] = useState(getDefaultSuggestions());
  const [isParsingIntake, setIsParsingIntake] = useState(false);

  const [protocol, setProtocol] = useState(null);
  const [questionQueue, setQuestionQueue] = useState([]); // question ids remaining
  const [answers, setAnswers] = useState({});
  const [answerHistory, setAnswerHistory] = useState([]); // [{ questionId, value }]
  const [numberDraft, setNumberDraft] = useState('');

  const inputRef = useRef(null);
  const bodyRef = useRef(null);

  // ── Suggestions update as user types ──────────────────────────────────────
  useEffect(() => {
    if (!inputText.trim()) {
      setSuggestions(getDefaultSuggestions());
    } else {
      const results = searchProtocols(inputText, 5);
      setSuggestions(results.map(r => r.protocol));
    }
  }, [inputText]);

  // ── Start triage from a protocol ──────────────────────────────────────────
  const startProtocol = useCallback((p, prefilledAnswers = {}, personTypeOverride = null) => {
    const finalPersonType = personTypeOverride || personType;
    const initialAnswers = { person_type: finalPersonType, ...prefilledAnswers };

    // Filter out questions already answered by AI prefill
    const remaining = (p.initialQuestions || []).filter(qId => !(qId in initialAnswers));

    setProtocol(p);
    setAnswers(initialAnswers);
    setQuestionQueue(remaining);
    setAnswerHistory([]);
    setPhase('questions');
  }, [personType]);

  // ── Handle intake submit ───────────────────────────────────────────────────
  const handleIntakeSubmit = useCallback(async () => {
    const text = inputText.trim();
    if (!text) return;

    // Emergency short-circuit before any API call
    if (isEmergencyText(text)) {
      navigate('getCareResult', {
        triageResult: {
          careLevel: 'emergency_now',
          reasonCodes: ['emergency_keyword_detected'],
          suggestedCareSites: ['er'],
          matchedRuleId: null,
          isDefault: false,
        },
        answers: { person_type: personType },
        protocolId: null,
        ageBand: null,
        personType,
      });
      return;
    }

    setIsParsingIntake(true);
    try {
      const intake = await parseIntake(text);
      const p = intake.symptomCluster ? getProtocol(intake.symptomCluster) : null;

      if (p) {
        const resolvedPersonType = intake.personType || personType;
        setPersonType(resolvedPersonType);
        startProtocol(p, intake.prefilledAnswers || {}, resolvedPersonType);
      } else {
        // No protocol matched — use first suggestion if available
        const firstSuggestion = suggestions[0];
        if (firstSuggestion) {
          startProtocol(firstSuggestion, {}, personType);
        }
        // else just stay on intake — user can tap a suggestion
      }
    } catch (err) {
      // Graceful degradation: try first suggestion
      const firstSuggestion = suggestions[0];
      if (firstSuggestion) startProtocol(firstSuggestion, {}, personType);
    } finally {
      setIsParsingIntake(false);
    }
  }, [inputText, personType, suggestions, startProtocol, navigate]);

  // ── Handle a question answer ──────────────────────────────────────────────
  const handleAnswer = useCallback((value) => {
    const currentQuestionId = questionQueue[0];
    if (!currentQuestionId) return;

    const newAnswers = { ...answers, [currentQuestionId]: value };
    const newHistory = [...answerHistory, { questionId: currentQuestionId, value }];
    const remaining = questionQueue.slice(1);

    // Early exit on emergency
    if (hasEmergencyRedFlag({ protocolId: protocol.id, answers: newAnswers })) {
      const result = evaluate({ protocolId: protocol.id, answers: newAnswers });
      const store = getState();
      navigate('getCareResult', {
        triageResult: result,
        answers: newAnswers,
        protocolId: protocol.id,
        ageBand: getAgeBand(newAnswers.age_years ?? store.age),
        personType,
      });
      return;
    }

    if (remaining.length === 0) {
      // All questions answered — evaluate
      const result = evaluate({ protocolId: protocol.id, answers: newAnswers });
      const store = getState();
      navigate('getCareResult', {
        triageResult: result,
        answers: newAnswers,
        protocolId: protocol.id,
        ageBand: getAgeBand(newAnswers.age_years ?? store.age),
        personType,
      });
      return;
    }

    setAnswers(newAnswers);
    setAnswerHistory(newHistory);
    setQuestionQueue(remaining);
    setNumberDraft('');
  }, [questionQueue, answers, answerHistory, protocol, navigate, personType]);

  // ── Go back to previous answer ────────────────────────────────────────────
  const handlePrevAnswer = useCallback(() => {
    if (answerHistory.length === 0) {
      // Back to intake
      setPhase('intake');
      setProtocol(null);
      setQuestionQueue([]);
      setAnswers({});
      setAnswerHistory([]);
      return;
    }
    const prev = answerHistory[answerHistory.length - 1];
    const newHistory = answerHistory.slice(0, -1);
    const newAnswers = { ...answers };
    delete newAnswers[prev.questionId];
    setAnswers(newAnswers);
    setAnswerHistory(newHistory);
    setQuestionQueue([prev.questionId, ...questionQueue]);
    setNumberDraft('');
  }, [answerHistory, answers, questionQueue]);

  // ── Progress ──────────────────────────────────────────────────────────────
  const totalQuestions = protocol ? (protocol.initialQuestions || []).length : 0;
  const answeredCount = Object.keys(answers).filter(k => k !== 'person_type').length;
  const progress = totalQuestions > 0 ? answeredCount / totalQuestions : 0;

  // ── Current question ──────────────────────────────────────────────────────
  const currentQuestionId = questionQueue[0];
  const currentQuestion = currentQuestionId ? QUESTIONS[currentQuestionId] : null;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={s.shell}>
      {/* Header */}
      <div style={s.header}>
        <button onClick={phase === 'questions' ? handlePrevAnswer : goBack} style={s.backBtn}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M12 5l-7 7 7 7" stroke="var(--ink)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {phase === 'questions' && answerHistory.length > 0 ? (
            <span style={s.backLabel}>Prev answer</span>
          ) : (
            <span style={s.backLabel}>Back</span>
          )}
        </button>

        <div style={s.headerCenter}>
          <span style={s.headerTitle}>Get Care Now</span>
        </div>

        {/* Fallback to old symptom screen */}
        <button onClick={() => navigate('symptom')} style={s.fallbackBtn}>
          AI chat
        </button>
      </div>

      {/* Progress bar (questions phase) */}
      {phase === 'questions' && (
        <div style={s.progressBar}>
          <div style={{ ...s.progressFill, width: `${Math.round(progress * 100)}%` }} />
        </div>
      )}

      {/* Body */}
      <div style={s.body} ref={bodyRef}>
        <div style={s.bodyInner}>

          {/* ── INTAKE PHASE ───────────────────────────────────────────────── */}
          {phase === 'intake' && (
            <>
              <div>
                <h1 style={s.intakeHeading}>What's going on?</h1>
                <p style={s.intakeSubhead}>Describe what you're feeling and we'll guide you to the right care.</p>
              </div>

              {/* Who is this for */}
              <div>
                <p style={s.sectionLabel}>Who is this for?</p>
                <PersonToggle value={personType} onChange={setPersonType} />
              </div>

              {/* Text input */}
              <div style={s.inputWrap}>
                <textarea
                  ref={inputRef}
                  style={s.textarea}
                  placeholder="e.g. my 4-year-old has had a fever for two days…"
                  value={inputText}
                  onChange={e => setInputText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey && inputText.trim()) {
                      e.preventDefault();
                      handleIntakeSubmit();
                    }
                  }}
                  rows={3}
                />
                <button
                  style={{
                    ...s.submitBtn,
                    opacity: inputText.trim() ? 1 : 0.45,
                  }}
                  disabled={!inputText.trim() || isParsingIntake}
                  onClick={handleIntakeSubmit}
                >
                  {isParsingIntake ? (
                    <span style={s.loadingDots}>
                      <span /><span /><span />
                    </span>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>

              {/* Suggestions */}
              <div>
                <p style={s.sectionLabel}>
                  {inputText.trim() ? 'Matching conditions' : 'Common right now'}
                </p>
                <div style={s.suggestionList}>
                  {suggestions.map(p => (
                    <SuggestionRow key={p.id} protocol={p} onSelect={p => startProtocol(p, {}, personType)} />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── QUESTIONS PHASE ────────────────────────────────────────────── */}
          {phase === 'questions' && currentQuestion && (
            <div style={s.questionWrap}>
              <div style={s.protocolBadge}>{protocol?.label}</div>

              <h2 style={s.questionText}>{currentQuestion.label}</h2>

              {currentQuestion.type === 'boolean' && (
                <BooleanQuestion question={currentQuestion} onAnswer={handleAnswer} />
              )}
              {currentQuestion.type === 'single_select' && (
                <SelectQuestion question={currentQuestion} onAnswer={handleAnswer} />
              )}
              {currentQuestion.type === 'number' && (
                <NumberQuestion
                  question={currentQuestion}
                  value={numberDraft}
                  onChange={setNumberDraft}
                  onSubmit={handleAnswer}
                />
              )}

              <p style={s.questionCount}>
                Question {answeredCount + 1} of {totalQuestions}
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  shell: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    background: 'var(--cream)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    borderBottom: '1px solid var(--border)',
    background: 'var(--surface)',
    flexShrink: 0,
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--ink)',
    padding: '4px 0',
    minWidth: 72,
  },
  backLabel: {
    fontFamily: 'var(--font-body)',
    fontSize: '13px',
    color: 'var(--ink-2)',
  },
  headerCenter: {
    flex: 1,
    textAlign: 'center',
  },
  headerTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: '17px',
    fontWeight: 700,
    color: 'var(--ink)',
  },
  fallbackBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
    fontSize: '13px',
    color: 'var(--teal)',
    padding: '4px 0',
    minWidth: 48,
    textAlign: 'right',
  },
  progressBar: {
    height: 3,
    background: 'var(--cream-2)',
    flexShrink: 0,
  },
  progressFill: {
    height: '100%',
    background: 'var(--teal)',
    transition: 'width 0.3s var(--ease-out)',
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  bodyInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
    padding: '24px 16px 48px',
  },
  intakeHeading: {
    fontFamily: 'var(--font-display)',
    fontSize: '26px',
    fontWeight: 700,
    color: 'var(--ink)',
    margin: '0 0 8px',
    lineHeight: 1.2,
  },
  intakeSubhead: {
    fontFamily: 'var(--font-body)',
    fontSize: '15px',
    color: 'var(--ink-3)',
    margin: 0,
    lineHeight: 1.5,
  },
  sectionLabel: {
    fontFamily: 'var(--font-body)',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--ink-4)',
    margin: '0 0 10px',
  },
  toggleRow: {
    display: 'flex',
    gap: 8,
  },
  toggleBtn: {
    flex: 1,
    padding: '10px 0',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
    fontSize: '14px',
    transition: 'background 0.15s, color 0.15s',
    WebkitTapHighlightColor: 'transparent',
  },
  inputWrap: {
    position: 'relative',
  },
  textarea: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '14px 52px 14px 16px',
    border: '1.5px solid var(--border-strong)',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-body)',
    fontSize: '15px',
    lineHeight: 1.5,
    color: 'var(--ink)',
    background: 'var(--surface)',
    resize: 'none',
    outline: 'none',
  },
  submitBtn: {
    position: 'absolute',
    right: 10,
    bottom: 10,
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'var(--teal)',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.15s',
  },
  loadingDots: {
    display: 'flex',
    gap: 3,
  },
  suggestionList: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
  },
  suggestionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    background: 'none',
    border: 'none',
    borderBottom: '1px solid var(--border)',
    cursor: 'pointer',
    textAlign: 'left',
    WebkitTapHighlightColor: 'transparent',
    width: '100%',
  },
  suggestionLabel: {
    fontFamily: 'var(--font-body)',
    fontSize: '15px',
    color: 'var(--ink)',
    fontWeight: 500,
  },
  // Question phase
  questionWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    paddingTop: 8,
  },
  protocolBadge: {
    display: 'inline-block',
    background: 'var(--teal-faint)',
    color: 'var(--teal)',
    fontFamily: 'var(--font-body)',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding: '4px 10px',
    borderRadius: 20,
    alignSelf: 'flex-start',
  },
  questionText: {
    fontFamily: 'var(--font-display)',
    fontSize: '22px',
    fontWeight: 700,
    color: 'var(--ink)',
    margin: 0,
    lineHeight: 1.3,
  },
  answerGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  answerStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  answerBtn: {
    padding: '16px',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
    fontSize: '16px',
    fontWeight: 700,
    WebkitTapHighlightColor: 'transparent',
    transition: 'opacity 0.1s',
  },
  answerBtnYes: {
    background: 'var(--teal)',
    color: '#fff',
  },
  answerBtnNo: {
    background: 'var(--cream-2)',
    color: 'var(--ink)',
  },
  selectBtn: {
    padding: '16px',
    border: '1.5px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
    fontSize: '15px',
    fontWeight: 500,
    color: 'var(--ink)',
    background: 'var(--surface)',
    textAlign: 'left',
    WebkitTapHighlightColor: 'transparent',
  },
  numberRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  numberInput: {
    flex: 1,
    padding: '14px 16px',
    border: '1.5px solid var(--border-strong)',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-body)',
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--ink)',
    background: 'var(--surface)',
    outline: 'none',
  },
  numberUnit: {
    fontFamily: 'var(--font-body)',
    fontSize: '14px',
    color: 'var(--ink-3)',
    whiteSpace: 'nowrap',
  },
  questionCount: {
    fontFamily: 'var(--font-body)',
    fontSize: '12px',
    color: 'var(--ink-4)',
    margin: 0,
    textAlign: 'center',
  },
};
